import {
  Box,
  Button,
  Collapse,
  Flex,
  FlexProps,
  useDisclosure,
} from "@chakra-ui/react";
import { useForm, DefaultValues } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ComponentType, ReactNode, useEffect, useMemo, useRef } from "react";
import { keyBy } from "lodash";
import {
  ClearableSearchInput,
  searchSchema,
} from "components/Shared/Forms/SearchInput";
import Form from "components/Shared/Forms/Form";
import createFormFieldComponents from "components/Shared/Forms/FormField";
import FilterEditor from "components/Shared/FilterEditor";
import {
  AppliedFilter,
  FilterDefinition,
} from "components/Shared/FilterEditor/utils";
import { getAppliedFiltersSchema } from "components/Shared/FilterEditor/schema";
import { asyncVoid } from "utils/asyncVoidHandler";
import ToggleFiltersButton from "components/Shared/FilterEditor/FilterBar/ToggleFiltersButton";
import {
  getAppliedSearchFilter,
  useValidateInitialData,
} from "components/Shared/FilterEditor/FilterBar/utils";
import Well from "components/Shared/Well";
import ApplyFiltersButton from "components/Shared/FilterEditor/FilterBar/ApplyFiltersButton";
import { SEARCH_FILTER_ID } from "components/Shared/FilterEditor/standardFilters";

export type FilterBarContainerComponentProps = {
  children: ReactNode;
  headerRight: ReactNode;
};

function DefaultFilterBarContainer({
  children,
  headerRight,
}: FilterBarContainerComponentProps) {
  return (
    <Flex flexWrap="wrap-reverse" justifyContent="flex-end" py={3} rowGap={2}>
      <Flex
        alignItems="center"
        py={1}
        px={4}
        flex="1 1 20rem"
        gap={2}
        mr={headerRight ? "auto" : undefined}
      >
        {children}
      </Flex>
      {headerRight}
    </Flex>
  );
}

export function FreestandingFilterBarContainer({
  children,
  headerRight,
  ...rest
}: FlexProps & FilterBarContainerComponentProps) {
  return (
    <Flex
      flexWrap="wrap-reverse"
      alignItems="center"
      mb={3}
      columnGap={2}
      rowGap={3}
      {...rest}
    >
      {children}
      <Flex ml="auto">{headerRight}</Flex>
    </Flex>
  );
}

export function FreestandingFilterWell({ children }: { children: ReactNode }) {
  return (
    <Box pb={4}>
      <Well borderRadius="md" p={3}>
        {children}
      </Well>
    </Box>
  );
}

type FiltersForm = {
  search: string | null;
  filters: AppliedFilter[];
};

function getFiltersSchema(
  filterDefinitions: FilterDefinition[],
): yup.ObjectSchema<FiltersForm> {
  return yup.object().shape({
    search: searchSchema,
    filters: getAppliedFiltersSchema(filterDefinitions),
  });
}

export type FilterBarWithSearchProps = {
  FilterBarContainerComponent?: ComponentType<FilterBarContainerComponentProps>;
  FiltersContainerComponent?: ComponentType<{ children: ReactNode }>;
  filterDefinitions: FilterDefinition[];
  filters?: AppliedFilter[];
  headerRight?: ReactNode;
  onFiltersChange: (filters: AppliedFilter[]) => void;
};

export default function FilterBarWithSearch({
  FilterBarContainerComponent = DefaultFilterBarContainer,
  FiltersContainerComponent = Well,
  filterDefinitions,
  filters,
  headerRight,
  onFiltersChange,
}: FilterBarWithSearchProps) {
  const schema = useMemo<yup.ObjectSchema<FiltersForm>>(
    () => getFiltersSchema(filterDefinitions),
    [filterDefinitions],
  );

  const filterDefinitionsById = useMemo(
    () => keyBy(filterDefinitions, "id"),
    [filterDefinitions],
  );

  const { FormField } = useMemo(
    () => createFormFieldComponents<FiltersForm>({ schema }),
    [schema],
  );

  const formRef = useRef<HTMLFormElement>(null);

  const initialData = useMemo<FiltersForm>(() => {
    const searchFilter = filters?.find((f) => f.filter === SEARCH_FILTER_ID);
    const otherFilters = filters?.filter(
      (f) =>
        f.filter !== SEARCH_FILTER_ID &&
        !filterDefinitionsById[f.filter]?.isHidden,
    );
    return {
      search:
        typeof searchFilter?.value === "string" ? searchFilter.value : null,
      filters: otherFilters || [],
    };
  }, [filters, filterDefinitionsById]);

  const formContext = useForm<FiltersForm>({
    resolver: yupResolver(schema),
    defaultValues: initialData as DefaultValues<FiltersForm>,
  });

  const {
    formState: { isDirty, isValid },
  } = formContext;

  const isInitialValid = useValidateInitialData({
    initialData,
    schema,
    onInvalid: () => asyncVoid(formContext.trigger()),
  });

  const { isOpen, onToggle } = useDisclosure({
    defaultIsOpen: !isInitialValid,
  });

  const searchValue = formContext.watch("search");

  useEffect(() => {
    formContext.reset(initialData);
  }, [initialData, formContext]);

  const formFilters = formContext.watch("filters");
  const numFormFilters = formFilters?.length;

  const handleValidSubmit = (data: FiltersForm) => {
    const filtersWithSearch = data.filters.slice(0);

    filters?.forEach((f) => {
      if (f.filter === SEARCH_FILTER_ID) return;
      const filterDef = filterDefinitionsById[f.filter];
      if (filterDef?.isHidden) {
        filtersWithSearch.push(f);
      }
    });

    if (data.search) {
      filtersWithSearch.push(getAppliedSearchFilter(data.search));
    }
    onFiltersChange(filtersWithSearch);
  };

  return (
    <Form {...{ formContext, formRef }} onValidSubmit={handleValidSubmit}>
      <FilterBarContainerComponent headerRight={headerRight}>
        <FormField
          name="search"
          label={null}
          my={0}
          minW="7em"
          maxW="20em"
          flex="1 0 7em"
          renderInput={(props) => (
            <ClearableSearchInput
              {...props}
              isClearDisabled={!searchValue}
              onClear={() => {
                formContext.setValue("search", null, {
                  shouldDirty: true,
                });
                formRef.current?.requestSubmit();
              }}
            />
          )}
        />

        <ToggleFiltersButton
          {...{ isOpen, isValid, isDirty }}
          numFilters={numFormFilters}
          onClick={onToggle}
        />

        <Button
          type="submit"
          colorScheme="pinkButton"
          variant="outline"
          size="xs"
          isDisabled={!isDirty}
          visibility={isOpen ? "hidden" : undefined}
        >
          Search
        </Button>
      </FilterBarContainerComponent>

      <Collapse in={isOpen} unmountOnExit>
        <FiltersContainerComponent>
          <FilterEditor
            {...{ filterDefinitions, formContext }}
            filtersFormName="filters"
            footerRight={<ApplyFiltersButton />}
            onClear={() => formRef.current?.requestSubmit()}
          />
        </FiltersContainerComponent>
      </Collapse>
    </Form>
  );
}
